import { useCallback, useState } from 'react';
import { useRouter } from 'next/navigation';
import toast from '@/components/atoms/toaster/helpers/toast';
import useTranslation from '@/providers/I18n/hooks/useTranslation';
import { ApiResult } from '@/types/lib/api';
import { Cart } from '@shared/types/cart';
import { Product } from '@shared/types/product';
import chunk from 'lodash/chunk';
import Papa from 'papaparse';
import useProductSearch from '@/lib/hooks/useProductSearch';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import useStores from '@/lib/hooks/useStores';
import useCart from '@/lib/hooks/useCart';
import { MAX_CART_ITEMS } from '@/providers/cart/types';
import { CsvParser } from '@/utils/parser';
import { Product as EntityProduct } from '@/types/entity/product';
import { CartType } from '@shared/types/cart/Cart';
import { Product as csvProduct } from '../../../../types';

/**
 * Convert the list of CSV products to the line‑item format expected by the API.
 * Each product carries its own commercialOperationKey from CSV parsing.
 */
const buildLineItems = (products: csvProduct[]) =>
  products.map(({ sku, quantity, commercialOperationKey }) => ({
    sku,
    count: quantity,
    isPreOrder: !!commercialOperationKey,
    ...(commercialOperationKey ? { commercialOperationKey } : {}),
  }));

/**
 * Parse every file selected by the customer and aggregate products / SKUs.
 */
const parseCSVFiles = (
  files: File[],
  readFiles: Record<string, string>,
  handleSKUsUpdate?: (skus: string[]) => void,
) => {
  const newProducts: csvProduct[] = [];
  const skus: string[] = [];
  files.forEach((file) => {
    const fileContent = readFiles[file.name];
    const delimiter = CsvParser.guessDelimiter(fileContent);

    const parsed = Papa.parse<string[]>(fileContent, {
      delimiter,
      quoteChar: '"',
      escapeChar: '"',
      skipEmptyLines: true,
      header: false,
    });

    const rows = parsed.data as string[][];
    const dataRows = rows.slice(1);

    dataRows.forEach((row) => {
      const [family, opKey, productKey, rootCode, name, status, sku, packaging, unitPrice, stock, quantity] = row;
      const quantityNbr = parseInt(quantity);
      const stockNbr = parseInt(stock);
      const packagingNbr = parseInt(packaging);
      if (!sku || isNaN(quantityNbr) || quantityNbr <= 0) return;

      const normalizedOpKey = opKey || undefined;

      skus.push(sku);

      const existing = newProducts.find((p) => p.sku === sku);
      if (existing) {
        existing.quantity += quantityNbr;
      } else {
        newProducts.push({
          family,
          productKey,
          rootCode,
          name,
          status,
          sku,
          packaging: packagingNbr,
          unitPrice,
          stock: stockNbr,
          quantity: quantityNbr,
          commercialOperationKey: normalizedOpKey,
        });
      }
    });
  });

  handleSKUsUpdate?.(skus);
  return { newProducts, skus };
};

const useProductHandlers = (
  readFiles: Record<string, string>,
  handleClearFiles: () => void,
  addToCart?: (
    lineItems: {
      sku: string;
      count: number;
      isPreOrder?: boolean;
      commercialOperationKey?: string | null;
    }[],
  ) => Promise<ApiResult<Cart> | undefined>,
  handleSKUsUpdate?: (skus: string[]) => void,
  onClose?: () => void,
) => {
  const [products, setProducts] = useState<csvProduct[]>([]);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [addToCartError, setAddToCartError] = useState<Record<string, string>>({});
  const [newCartType, setNewCartType] = useState<CartType>(CartType.Default);

  const router = useRouter();
  const { translate } = useTranslation();
  const { loadManually } = useProductSearch();
  const { channel, priceChannel } = useStores();
  const { selectedBusinessUnit, selectedStore } = useStoreAndBusinessUnits();

  const {
    updateCustomField,
    cart: currentCart,
    computeCartType,
  } = useCart(selectedBusinessUnit?.key, selectedStore?.key);
  const memoizedClearFiles = useCallback(handleClearFiles, [handleClearFiles]);

  const handleUploadError = useCallback(
    (key: string) => {
      const errorMessages: Record<string, string> = {
        empty: translate('quick-order.error.no.items'),
        exceeds: translate('quick-order.error.cart.items.exceeds'),
        outdated: translate('quick-order.error.catalog.outdated'),
      };
      setAddToCartError({ [key]: errorMessages[key] });
      setLoading(false);
    },
    [translate],
  );

  const addBatchesSequentially = useCallback(
    async (
      batches: {
        sku: string;
        count: number;
        isPreOrder?: boolean;
        commercialOperationKey?: string | null;
      }[][],
      updateCartType: boolean,
      isPreOrder = false,
      commercialOperationKey?: string | null,
    ) => {
      try {
        for (const batch of batches) {
          const resp = await addToCart?.(batch);
          if (!resp?.success) {
            handleUploadError('outdated');
            return false;
          }
        }
        if (updateCartType) {
          updateCustomField('isPreOrder', isPreOrder);
          updateCustomField('cartType', newCartType);
          if (commercialOperationKey) {
            updateCustomField('label', commercialOperationKey);
          }
        }
        setAddToCartError({});
        setLoading(false);
        onClose?.();
        setProducts([]);
        memoizedClearFiles();
        setNewCartType(CartType.Default);
        router.refresh();
        router.push('/cart');
        toast.success(translate('quick-order.quick.add.success'), {
          position: 'top-right',
        });
        return true;
      } catch {
        setLoading(false);
        toast.error(translate('common.something.went.wrong'), {
          position: 'top-right',
        });
        return false;
      }
    },
    [addToCart, handleUploadError, memoizedClearFiles, newCartType, onClose, router, translate],
  );

  const validateBeforeAddToCart = (
    newProducts: csvProduct[],
    fetchedProducts: Product[],
    currentCartItemsLength: number,
  ): boolean => {
    const exceedsMax =
      newProducts.length > MAX_CART_ITEMS || currentCartItemsLength + fetchedProducts.length > MAX_CART_ITEMS;

    if (exceedsMax) {
      handleUploadError('exceeds');
      return false;
    }

    if (fetchedProducts.length !== newProducts.length) {
      handleUploadError('outdated');
      return false;
    }

    return true;
  };

  const handleUploadClick = async (files: File[]) => {
    const { newProducts, skus } = parseCSVFiles(files, readFiles, handleSKUsUpdate);

    if (skus.length === 0) {
      handleUploadError('empty');
      return;
    }

    setProducts(newProducts);
    setLoading(true);
    setAddToCartError({});

    const fetchedProducts = await loadManually({
      skus,
      storeKey: selectedStore?.key,
      customerCategoryChannelKey: channel?.key,
      distributionChannelId: priceChannel?.channelId,
    });

    if (!fetchedProducts.length) {
      handleUploadError('outdated');
      return;
    }
    const currentCartItemsLength = currentCart?.lineItems?.length ?? 0;

    const firstOpKey = newProducts.find((p) => p.commercialOperationKey)?.commercialOperationKey;
    const isPreOrder = !!firstOpKey;
    const itemsForCartType = newProducts.map((np) => {
      const fetched = fetchedProducts.find((fp) => fp.variants?.some((v) => v.sku === np.sku));
      return {
        isPreOrder: !!np.commercialOperationKey,
        isDangerousMaterial: !!fetched?.variants?.[0]?.attributes?.isDangerousMaterial,
        specifications: [{ label: 'type', value: fetched?.variants?.[0]?.attributes?.type?.key ?? '' }],
      } as EntityProduct;
    });
    const computedCartType = computeCartType(itemsForCartType);
    setNewCartType(computedCartType);

    if (!validateBeforeAddToCart(newProducts, fetchedProducts, currentCartItemsLength)) {
      return;
    }

    const lineItems = buildLineItems(newProducts);
    const batches = chunk(lineItems, 500);
    await addBatchesSequentially(batches, true, isPreOrder, firstOpKey);
  };

  const onCheckboxChange = (product: csvProduct, value: boolean) => {
    const newChecked = { ...checked };
    newChecked[product.sku] = value;
    setChecked(newChecked);
  };

  const handleProductClear = () => setProducts([]);

  const handleAddToCart = () => {
    setLoading(true);
    const inStockProducts = products.filter((product) => checked[product.sku] && checked[product.quantity]);

    const lineItems = buildLineItems(inStockProducts);
    addToCart?.(lineItems).then(() => {
      setLoading(false);
      onClose?.();
      handleProductClear();
      memoizedClearFiles();
    });
  };

  const handleClearAddToCartError = () => {
    setAddToCartError({});
  };

  return {
    loading,
    checked,
    products,
    addToCartError,
    handleUploadClick,
    handleProductClear,
    handleAddToCart,
    onCheckboxChange,
    handleClearAddToCartError,
  };
};
export default useProductHandlers;
